// eslint-disable-next-line no-restricted-imports
import styled from '@emotion/styled';
import { FLAGS, useFlag } from '@iheartjane/feature-flags';
import {
  getDiscountLabel,
  getReservationModeLabel,
  getSpecialType,
  hasSingleReservationMode,
} from '@iheartjane/menu';
import {
  Box,
  Card,
  Flex,
  Image,
  Link,
  Typography,
  useMobileMediaQuery,
} from '@iheartjane/reefer';
import { shadows } from '@iheartjane/reefer-emotion';
import { useState } from 'react';

import { JumboDiscountBadge } from '@jane/shared-ecomm/components';
import { useMenu } from '@jane/shared-ecomm/providers';
import type { StoreSpecial } from '@jane/shared/types';

const MobileDetailContainer = styled.div({
  boxShadow: shadows.divider,
});

interface SpecialDetailHeaderProps {
  special: StoreSpecial;
}

export const MenuSpecialDetailHeader = ({
  special,
}: SpecialDetailHeaderProps) => {
  const [showTerms, setShowTerms] = useState(false);
  const isMobile = useMobileMediaQuery({});
  const { store } = useMenu();
  const extendedDealsLabelsEnabled = useFlag(FLAGS.c1ExtendedDealsLabels);

  const { title, description, photo, terms } = special;

  const specialPhoto = photo?.urls?.original || null;
  const headerLabel = getSpecialType(
    special.special_type,
    store?.store_compliance_language_settings,
    extendedDealsLabelsEnabled
  );

  const reservationMode = hasSingleReservationMode(special?.reservation_modes);
  const specialAvailability =
    reservationMode &&
    getReservationModeLabel(reservationMode, store?.reservation_mode_labels);

  if (isMobile) {
    return (
      <MobileDetailContainer>
        <Flex height="200px" justifyContent="center" alignItems="center">
          {!specialPhoto && (
            <JumboDiscountBadge color="info" textColor="text-info">
              {special.custom_badge
                ? special.custom_badge
                : getDiscountLabel({
                    discountAmount: special.discount_amount,
                    discountType: special.discount_type,
                    specialType: special.special_type,
                    storeComplianceLanguageSettings:
                      store?.store_compliance_language_settings,
                  })}
            </JumboDiscountBadge>
          )}

          {specialPhoto && (
            <Image
              height="100%"
              src={specialPhoto}
              altText={`${special.title} special photo`}
              responsive
              sizes="card"
            />
          )}
        </Flex>

        <Box p={24}>
          <Flex>
            <Typography variant="body-semibold" color="info" as="p" mb={8}>
              {headerLabel}
            </Typography>

            {specialAvailability && (
              <Typography
                color="text-light"
                variant="small-body-regular"
                ml="auto"
              >
                {specialAvailability} only
              </Typography>
            )}
          </Flex>

          <Typography branded variant="h2-semibold">
            {title}
          </Typography>

          <Typography>{description}</Typography>
          {terms && (
            <Typography as="span">
              <Link onClick={() => setShowTerms((prevValue) => !prevValue)}>
                Terms and Conditions
              </Link>
            </Typography>
          )}

          {terms && showTerms && <Typography>{terms}</Typography>}
        </Box>
      </MobileDetailContainer>
    );
  }

  return (
    <Card border="grays-light" width="100%" flat>
      <Card.Group direction="row" width="100%">
        {specialPhoto ? (
          <Card.Image
            src={specialPhoto}
            alt={`${special.title} special photo`}
            height="100%"
            width="320px"
            responsive
            sizes="card"
          />
        ) : (
          <Card.Content width="320px">
            <Flex
              alignItems="center"
              height="100%"
              justifyContent="center"
              px={24}
              py={40}
              width="100%"
            >
              <JumboDiscountBadge color="info" textColor="text-info">
                {special.custom_badge
                  ? special.custom_badge
                  : getDiscountLabel({
                      discountAmount: special.discount_amount,
                      discountType: special.discount_type,
                      specialType: special.special_type,
                      storeComplianceLanguageSettings:
                        store?.store_compliance_language_settings,
                    })}
              </JumboDiscountBadge>
            </Flex>
          </Card.Content>
        )}
        <Card.Content>
          <Box p={24}>
            <Flex>
              <Typography variant="h2-semibold" color="info">
                {headerLabel}
              </Typography>

              {specialAvailability && (
                <Typography
                  color="text-light"
                  variant="small-body-regular"
                  ml="auto"
                >
                  {specialAvailability} only
                </Typography>
              )}
            </Flex>
            <Typography branded variant="h1-semibold" mb={16}>
              {title}
            </Typography>

            <Typography>{description}</Typography>
            {terms && (
              <Link
                typography="body-regular"
                onClick={() => setShowTerms((prevValue) => !prevValue)}
              >
                Terms and Conditions
              </Link>
            )}

            {terms && showTerms && <Typography>{terms}</Typography>}
          </Box>
        </Card.Content>
      </Card.Group>
    </Card>
  );
};
